import React, { useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import debounce from 'lodash.debounce';
import { useParams, useNavigate } from 'react-router-dom';
import { useReport } from '../../contexts/ReportContext';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Edit from '@mui/icons-material/Edit';
import Error from '@mui/icons-material/Error';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { reportConfigs } from '../../reports/reportConfigs';

const StepIconComponent = ({ status, active }) => {
  if (active) {
    // Current step is green, unless there's an error
    if (status === 'error') return <Error color="error" />;
    if (status === 'completed') return <CheckCircle color="success" />;
    return <Edit color="success" />;
  }
  // Non-active steps
  if (status === 'completed') return <CheckCircle color="primary" />; // Completed is blue
  if (status === 'error') return <Error color="error" />;
  if (status === 'in-progress') return <Edit color="action" />;
  return <RadioButtonUnchecked color="disabled" />;
};

const ReportWizard = ({ reportConfig: propConfigs }) => {
  // Router & context
  const { reportType, reportId, stepId } = useParams();
  const reportConfig = propConfigs ?? reportConfigs[reportType];
  const navigate = useNavigate();
  const {
    loading,
    reportData,
    saveStep,
    stepStatuses,
    setReportId,
    setReportData,
  } = useReport();

  // Ensure provider knows which document we’re editing
  useEffect(() => {
    if (reportId) setReportId(reportId);
  }, [reportId, setReportId]);

  // Steps + activeStep state are needed regardless of loading
  const steps = reportConfig?.steps ?? [];
  const [activeStep, setActiveStep] = useState(() => {
    const idx = steps.findIndex((s) => s.id === stepId);
    return idx !== -1 ? idx : 0;
  });

  // Keep activeStep in sync with URL
  useEffect(() => {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx !== -1) {
      setActiveStep(idx);
    } else if (steps[0]) {
      navigate(`/${reportType}/${reportId}/${steps[0].id}`, { replace: true });
    }
  }, [stepId, steps, reportType, reportId, navigate]);

  // Formik – always initialise (hook order!)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: reportData,
    validationSchema: reportConfig?.validationSchema,
    onSubmit: () => {},
  });

  // Debounced function to trigger calculations
  const debouncedCalculate = useCallback(
    debounce((values) => {
      //console.log('[Wizard.jsx]  debounce: Triggering calculations with values:', values);
      setReportData(values, true); // true to run calculations
    }, 500),
    [setReportData]
  );

  // When formik values change, update context immediately (no calcs)
  // and trigger the debounced calculation.
  useEffect(() => {
    //console.log('[Wizard.jsx] useEffect[formik.values]: Formik values changed. Syncing to context without calcs.', formik.values);
    setReportData(formik.values, false); // false to avoid immediate calculation
    debouncedCalculate(formik.values);

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedCalculate.cancel();
    };
  }, [formik.values, setReportData, debouncedCalculate]);

  if (! reportConfig) {
    return <div>No configuration found for type: {reportType}</div>
  }

  // Early‑return **after hooks have executed**
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Helpers -------------------------------------------------------
  const handleSaveCurrentStep = () => {
    //console.log('[Wizard.jsx] handleSaveCurrentStep: Saving step with formik values:', formik.values);
    debouncedCalculate.cancel(); // Cancel any pending calculations before saving
    return saveStep(formik.values, activeStep, reportConfig, reportType);
  }

  const handleNext = async () => {
    //console.log('[Wizard.jsx] handleNext: Clicked.');
    await handleSaveCurrentStep();
    if (activeStep < steps.length - 1) {
      navigate(`/${reportType}/${reportId}/${steps[activeStep + 1].id}`);
    } else {
      formik.submitForm();
    }
  };

  const handleBack = async () => {
    //console.log('[Wizard.jsx] handleBack: Clicked.');
    await handleSaveCurrentStep();
    if (activeStep > 0) {
      navigate(`/${reportType}/${reportId}/${steps[activeStep - 1].id}`);
    }
  };

  const handleStepClick = async (idx) => {
    if (idx === activeStep) return;
    //console.log(`[Wizard.jsx] handleStepClick: Clicked to step ${idx}.`);
    await handleSaveCurrentStep();
    navigate(`/${reportType}/${reportId}/${steps[idx].id}`);
  };

  const ActiveStepComponent = steps[activeStep]?.component;
  if (!ActiveStepComponent) return <div>Wizard step not found.</div>;

return (
  /* full-height flex column so we can pin the button bar */
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
        {steps.map((step, idx) => (
          <Step
            key={step.id}
            onClick={() => handleStepClick(idx)}
            sx={{ cursor: 'pointer' }}
          >
            <StepLabel
              slots={{ stepIcon: StepIconComponent }}
              slotProps={{
                stepIcon: {
                  status: stepStatuses[idx],
                  active: idx === activeStep,
                },
              }}
              sx={{
                flexDirection: 'column',
                '.MuiStepLabel-label': {
                  marginTop: 1,
                  fontSize: '1.1rem',
                },
                '.MuiStepIcon-root': {
                  fontSize: '1.7rem',
                },
              }}
            >
              {step.name}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box
        /* grow to fill the space between stepper and button bar */
        sx={{
          flex: '1 1 auto',
          overflow: 'auto',
          px: 2,               // horizontal padding inside the scroll area
          pb: 6
        }}
      >
        {/* add a subtle frame around every step */}
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
          }}
        >
          <ActiveStepComponent formik={formik} />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          py: 3,
          /* ▶ sticks to bottom of its flex parent while
               the form above scrolls independently        */
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',  // keep it opaque while scrolling
        }}
      >

        {activeStep > 0 && 
        <Button variant='outlined' color='primary'
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          Back
        </Button>
        }
        <Box sx={{ flex: '1 1 auto' }} />

        {activeStep !== steps.length - 1 && (
          <Button onClick={handleNext} variant='contained' color='primary'>
          Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ReportWizard;